<template>
  <div class="w-full">
    <div
      class="w-full border min-h-[260px] p-5 relative flex justify-center items-center bg-white"
      :class="{
        'border border-danger border-dashed': error,
        'border-gray-300': !error,
      }"
    >
      <template v-if="!loading && files.length == 0">
        <div
          class="h-full w-full flex items-center justify-center flex-col cursor-pointer group"
          @click="chooseFile()"
        >
          <nuxt-img
            loading="lazy"
            src="/icons/file-upload.png"
            alt="upload-document"
            :width="64"
          />
          <p
            class="mt-2.5 mb-4 font-semibold group-hover:text-success text-base transition-colors ease-in duration-100"
          >
            {{ dataLocales.data.title }}
          </p>
          <p class="text-gray-700 text-sm">
            {{ dataLocales.data.description }}
          </p>
          <div class="h-8">
            <p class="text-sm text-danger mt-2" v-if="error">
              {{ dataLocales.data.err_msg }}
            </p>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="w-full px-6 flex items-end justify-center flex-col gap-4">
          <!-- upload loading -->
          <draggable
            v-model="files"
            group="images"
            :disabled="qrcreate.imageData"
            @start="drag = true"
            class="w-full"
            @end="drag = false"
            item-key="id"
          >
            <template #item="{ element }">
              <div
                class="w-full bg-white/90 h-24 border border-gray-200 border-dashed drop-shadow-sm hover:drop-shadow-lg rounded-md flex items-center px-5 py-3 group"
              >
                <div class="w-11 flex-shrink-0 mr-8">
                  <nuxt-img
                    loading="lazy"
                    :src="MimetypeIcon(element.mimetype)"
                    alt="file-document"
                    class="w-full"
                  />
                </div>
                <div class="flex flex-col">
                  <span
                    class="max-w-xl line-clamp-1 text-black leading-8"
                    style="word-break: break-word"
                  >
                    {{ element.originalname }}
                  </span>
                  <span class="text-sm text-grow">
                    {{ utils.convertBytes(element.size) }}
                  </span>
                </div>
                <div class="ml-auto flex-shrink-0">
                  <XIcon
                    v-if="!qrcreate.imageData"
                    @click="DeleteFile(element)"
                    class="w-5 h-5 cursor-pointer text-gray-500 group-hover:block hidden"
                  />
                </div>
              </div>
            </template>
          </draggable>
          <div
            v-if="loading"
            class="w-full bg-white/90 h-24 border border-gray-200 border-dashed drop-shadow-sm hover:drop-shadow-lg rounded-md flex items-center px-5 py-3 group"
          >
            <div class="w-11 flex-shrink-0 mr-8">
              <IconsLoadingDecorator class="w-8" />
            </div>
            <div class="w-full bg-gray-200 h-2 rounded-md relative">
              <span
                class="absolute left-0 top-0 h-2 bg-success rounded-md"
                :style="{ width: percentage + '%' }"
              ></span>
            </div>
            <div class="ml-auto flex-shrink-0 w-12 text-center">
              {{ Math.round(percentage) }}%
            </div>
          </div>
          <button
            @click="chooseFile()"
            v-if="!qrcreate.imageData"
            class="mt-6 h-11 w-full bg-gray-200 text-black hover:bg-gray-300 flex items-center justify-center gap-2"
          >
            <TransformIcon stroke-width="1" />
            <span>{{ $t("change_files") }}</span>
          </button>
        </div>
      </template>
      <input
        type="file"
        ref="file"
        @change="ChangeFile"
        accept="application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint,text/plain, application/pdf"
        class="hidden"
      />
    </div>
    <partials-expired-file-notice />
    <PartialsLinkQrcode />
    <tools-button-create @generate="generate" @clearSession="clearSession" />
  </div>
</template>
<script lang="ts">
import { useQrCreateStore } from "~/stores/qrcreate";
import uploadMixin from "./upload.mixin";
import { notify } from "@kyvg/vue3-notification";
import DataLocale from "~~/config/interface/DataLocale";
import { useLimit } from "~~/stores/limit";
export default defineComponent({
  mixins: [uploadMixin],
  data() {
    return {
      type: "document",
    };
  },
  setup() {
    const dataLocales = new DataLocale([
      {
        locale: "en",
        title: "Upload document files",
        description:
          "It supports uploading multiple formats, and the upload limit for a single file is 15M.",
        err_msg: " Please click here to upload the file first",
      },
      {
        locale: "vi",
        title: "Tải lên tệp tài liệu",
        description:
          "Hỗ trợ tải lên nhiều định dạng và giới hạn tải lên cho một tệp đơn là 15M.",
        err_msg: "Vui lòng nhấp vào đây để tải tệp lên trước",
      },
      {
        locale: "fr",
        title: "Télécharger des fichiers de document",
        description:
          "Il prend en charge le téléchargement de plusieurs formats et la limite de téléchargement pour un seul fichier est de 15 Mo.",
        err_msg: "Veuillez cliquer ici pour télécharger le fichier d'abord",
      },
      {
        locale: "es",
        title: "Cargar archivos de documento",
        description:
          "Admite la carga de múltiples formatos, y el límite de carga para un solo archivo es de 15M.",
        err_msg: "Haga clic aquí para cargar el archivo primero",
      },
      {
        locale: "it",
        title: "Carica file di documenti",
        description:
          "Supporta il caricamento di formati multipli e il limite di caricamento per un singolo file è di 15M.",
        err_msg: "Fare clic qui per caricare prima il file",
      },
      {
        locale: "nl",
        title: "Upload documentbestanden",
        description:
          "Het ondersteunt het uploaden van meerdere formaten en de uploadlimiet voor een enkel bestand is 15M.",
        err_msg: "Klik hier om eerst het bestand te uploaden",
      },
      {
        locale: "pt",
        title: "Carregar arquivos de documento",
        description:
          "Suporta o carregamento de vários formatos e o limite de carregamento para um único arquivo é de 15M.",
        err_msg: "Clique aqui para carregar o arquivo primeiro",
      },
      {
        locale: "ja",
        title: "文書ファイルのアップロード",
        description:
          "複数の形式をサポートし、単一ファイルのアップロード制限は15Mです。",
        err_msg: "最初にここをクリックしてファイルをアップロードしてください",
      },
      {
        locale: "zh",
        title: "上传文档文件",
        description: "支持上传多种格式，单个文件的上传限制为15M。",
        err_msg: "请先点击此处上传文件",
      },
      {
        locale: "de",
        title: "Dokumentendateien hochladen",
        description:
          "Es unterstützt das Hochladen mehrerer Formate, und das Upload-Limit für eine einzelne Datei beträgt 15 MB.",
        err_msg: "Bitte klicken Sie hier, um zuerst die Datei hochzuladen.",
      },
    ]);
    const qrcreate = useQrCreateStore();
    const qrstyle = useQrStyle();
    const utils = useUtils();
    const limit = useLimit()
    return {
      qrcreate,
      qrstyle,
      error: ref(false),
      init: false,
      utils,
      dataLocales,
      limit
    };
  },
  methods: {
    chooseFile() {
      this.error = false;
      (this.$refs.file as HTMLInputElement).click();
    },
    validateData() {
      if (this.files.length) {
        return true;
      }
      this.error = true;
      return false;
    },
    async ChangeFile(e: Event) {
      if (this.limit.checkLimit("document")) {
        return;
      }
      var files = (e.target as HTMLInputElement).files;
      if (files?.length) {
        var formdata = new FormData();
        for (let i = 0; i < files.length; i++) {
          if (files[i].size > 15 * 1024 * 1024) {
            notify({
              type: "warn",
              text: "Please reduce its size to less than 15mb",
              title: "Sorry, the file has exceeded the upload limit",
            });
            return;
          }
          formdata.append("file", files[i]);
        }

        this.$forceUpdate();
        if (this.files[0] && this.files[0].publicId) {
          this.DeleteFile(this.files[0]);
        }
        await this.UploadFiles(formdata);
        (this.$refs.file as HTMLInputElement).value = "";
      }
    },
    async generate() {
      if (this.validateData()) {
        await this.qrcreate.CreateSession(this.type, this.files);
      }
    },
    MimetypeIcon(type: string) {
      if (type.includes("word")) {
        return "/types/file_doc.png";
      }
      if (type.includes("excel")) {
        return "/types/file_xls.png";
      }
      if (type.includes("ppt")) {
        return "/types/file_ppt.png";
      }
      if (type.includes("pdf")) {
        return "/types/file_pdf.png";
      }
      return "/types/file_txt.png";
    },
  },
});
</script>
